import {
  Sparkles,
  Heart,
  ShieldCheck,
  Palette,
  Brush,
  Scissors,
  BadgeCheck,
  ArrowRight,
} from "lucide-react";
import { motion } from "framer-motion";
import BannerAd from '../../components/AdScript';

// Compact hero: headline + category quick-filters + a small decorative
// craft tile on large screens. Kept slim so products show up fast.
const ShopHero = ({
  categories = [],
  selectedCategory = "All",
  onSelectCategory,
}) => {
  // Show "All" plus up to 5 real categories as quick filters
  const quickFilters = ["All", ...categories.filter((c) => c !== "All").slice(0, 5)];

  // Decorative craft tiles (visual only)
  const craftTiles = [
    { icon: Brush, label: "Handloom", color: "from-rose-100 to-pink-50 text-rose-500" },
    { icon: Palette, label: "Terracotta", color: "from-amber-100 to-orange-50 text-amber-500" },
    { icon: Scissors, label: "Stitched", color: "from-purple-100 to-indigo-50 text-purple-500" },
    { icon: Heart, label: "Made with Love", color: "from-emerald-100 to-teal-50 text-emerald-500" },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-white via-rose-50 to-purple-50">
      {/* Accent line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-400 via-purple-400 to-blue-400"></div>

      {/* Decorative glow blobs */}
      <div className="absolute -top-10 -right-10 w-52 h-52 bg-gradient-to-r from-rose-100 to-purple-100 rounded-full opacity-40 blur-2xl"></div>
      <div className="absolute -bottom-10 -left-10 w-52 h-52 bg-gradient-to-r from-amber-100 to-yellow-100 rounded-full opacity-40 blur-2xl"></div>

      <div className="container mx-auto px-4 py-8 md:py-10">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-10">
          {/* Left - Message & Quick Filters */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:flex-1 relative z-10"
          >
            {/* Eyebrow pill */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm border border-rose-100 shadow-sm mb-4">
              <Sparkles className="w-3.5 h-3.5 text-rose-500" />
              <span className="text-xs font-semibold tracking-wide text-rose-600">
                তন্তিকা · Handmade with love in Bengal
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl xl:text-5xl font-extrabold text-gray-900 leading-tight mb-3">
              Handcrafted{" "}
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-rose-500 bg-clip-text text-transparent">
                Treasures
              </span>
              <br />
              from Bengal's Artisans
            </h1>

            {/* Subtext */}
            <p className="text-gray-600 text-sm sm:text-base max-w-xl mb-5">
              Authentic, one-of-a-kind pieces crafted by master artisans. Each
              creation carries the soul of its maker — find the story that
              speaks to you.
            </p>

            {/* Category Quick Filters */}
            {quickFilters.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {quickFilters.map((cat, i) => {
                  const isActive = selectedCategory === cat;
                  return (
                    <motion.button
                      key={cat}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + i * 0.05 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => onSelectCategory && onSelectCategory(cat)}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md shadow-blue-200"
                          : "bg-white/80 text-gray-700 border border-gray-200 shadow-sm hover:border-blue-300 hover:bg-white"
                      }`}
                    >
                      {isActive && <Sparkles className="w-3.5 h-3.5" />}
                      {cat}
                      {cat === "All" && !isActive && (
                        <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                      )}
                    </motion.button>
                  );
                })}
              </div>
            )}
          </motion.div>

          {/* Right - Decorative craft tiles (large screens only) */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="hidden lg:block lg:w-72 xl:w-80 shrink-0 relative z-10"
          >
            <div className="relative">
              <div className="grid grid-cols-2 gap-3">
                {craftTiles.map((tile, index) => {
                  const Icon = tile.icon;
                  return (
                    <motion.div
                      key={tile.label}
                      animate={{ y: [0, -6, 0] }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: index * 0.4,
                      }}
                      className={`rounded-2xl p-4 bg-gradient-to-br ${tile.color} border border-white/70 shadow-sm flex flex-col items-start justify-between min-h-[104px]`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-xs font-semibold text-gray-700">
                        {tile.label}
                      </span>
                    </motion.div>
                  );
                })}
              </div>

              {/* Floating badges */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 2.5, repeat: Infinity }}
                className="absolute -top-3 -right-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1"
              >
                <BadgeCheck className="w-3.5 h-3.5" />
                100% Handmade
              </motion.div>
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 0.6 }}
                className="absolute -bottom-3 -left-2 bg-white text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-full shadow-md border border-gray-100 flex items-center gap-1"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-blue-500" />
                Artisan Direct
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Ad strip - kept slim */}
        <div className="mt-6 flex justify-center">
          <BannerAd
            key="708f1310e8b739077a59073d869d1360"
            height={90}
            width={728}
            className="rounded-lg shadow-md"
          />
        </div>
      </div>
    </section>
  );
};

export default ShopHero;
